import { z } from "zod";

/**
 * The school profile captured at signup and edited in Settings.
 *
 * ONE schema, imported by the API (`POST /onboarding/quickstart`,
 * `PATCH /settings/school`) and by the web forms, so the two can never drift
 * on what a valid phone number or social URL is — the non-negotiable is "Zod
 * on both client and server", and two copies of a schema are not one schema.
 *
 * Mandatory: name, address, phone, email. Everything else — the second phone,
 * the social links and the map pin — is something a school can come back and
 * fill in later, so it is optional here and the form says so rather than
 * blocking the first sign-in on it.
 */

/** Trim, and treat a field the user cleared as absent rather than as "". */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === "" ? undefined : v))
    .optional();

/**
 * Deliberately permissive: +country codes, spaces, dashes and brackets are all
 * how people actually type a number, and this form is the first thing a school
 * ever does. 7–15 digits is E.164's own range.
 */
export const phoneSchema = z
  .string()
  .trim()
  .min(1, "Phone number is required")
  .refine((v) => /^[+]?[\d\s\-()]{7,20}$/.test(v) && (v.match(/\d/g) ?? []).length >= 7 && (v.match(/\d/g) ?? []).length <= 15, {
    message: "Enter a valid phone number",
  });

const optionalPhoneSchema = z
  .string()
  .trim()
  .transform((v) => (v === "" ? undefined : v))
  .optional()
  .refine((v) => v === undefined || (/^[+]?[\d\s\-()]{7,20}$/.test(v) && (v.match(/\d/g) ?? []).length >= 7), {
    message: "Enter a valid phone number",
  });

/**
 * A social link is a URL. Accepting a bare handle would mean guessing the
 * platform's URL shape for six platforms, and guessing wrong puts a broken
 * link on a school's own profile.
 */
const socialUrl = z
  .string()
  .trim()
  .transform((v) => (v === "" ? undefined : v))
  .optional()
  .refine((v) => v === undefined || /^https?:\/\/.+\..+/.test(v), {
    message: "Enter a full link, starting with https://",
  });

export const schoolSocialsSchema = z.object({
  website:   socialUrl,
  facebook:  socialUrl,
  instagram: socialUrl,
  twitter:   socialUrl,
  linkedin:  socialUrl,
  youtube:   socialUrl,
});

export type SchoolSocials = z.infer<typeof schoolSocialsSchema>;

export const schoolProfileSchema = z.object({
  /** Required — a school without these cannot be contacted or found. */
  address: z.string().trim().min(1, "Address is required").max(500),
  phone:   phoneSchema,
  email:   z.string().trim().min(1, "Email is required").email("Enter a valid email address").max(200),

  /** Optional, and genuinely so — the form must not gate sign-in on them. */
  city:    optionalText(120),
  state:   optionalText(120),
  pincode: optionalText(20),
  phoneAlt: optionalPhoneSchema,
  socials: schoolSocialsSchema.optional(),

  /**
   * The map pin. Optional and addable later; stored as real columns rather
   * than in the settings blob because coordinates are queryable data (nearest
   * branch, transport routing) and a JSON blob cannot be indexed.
   */
  latitude:  z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

export type SchoolProfileInput = z.infer<typeof schoolProfileSchema>;

/**
 * CAMP-2026-0955 — what Settings → School Profile requires on top of the
 * onboarding contract: Contact Details (email, phone) AND the whole Location
 * block (street, city, state, pincode) must be filled in.
 *
 * It is a SEPARATE schema on purpose. `schoolProfileSchema` is also the
 * signup (`POST /onboarding/quickstart`) contract, where city/state/pincode
 * are optional so first sign-in is not gated on them; making them required
 * there would break onboarding. Settings uses these pieces two ways:
 *   - the web form validates the whole set with `schoolProfileRequiredSchema`
 *     before it saves, and sends all six keys;
 *   - `PATCH /settings/school` merges `schoolProfileRequiredSchema.partial()`,
 *     so a partial PATCH from another screen (hall-ticket hold, grade scales,
 *     money reads…) that does not mention these keys is untouched, but a key
 *     that IS sent can never be blank or malformed.
 */
export const pincodeSchema = z
  .string()
  .trim()
  .min(1, "Pincode is required")
  .regex(/^[1-9]\d{5}$/, "Enter a valid 6-digit pincode");

export const schoolProfileRequiredSchema = z.object({
  email:   schoolProfileSchema.shape.email,
  phone:   schoolProfileSchema.shape.phone,
  address: z.string().trim().min(1, "Street address is required").max(500),
  city:    z.string().trim().min(1, "City is required").max(120),
  state:   z.string().trim().min(1, "State is required").max(120),
  pincode: pincodeSchema,
});

export type SchoolProfileRequired = z.infer<typeof schoolProfileRequiredSchema>;
export type SchoolProfileRequiredKey = keyof SchoolProfileRequired;

/**
 * Validate the six mandatory fields as a set and return one message per
 * failing field (empty object = valid). Missing/undefined counts as blank.
 */
export function validateSchoolProfileRequired(
  values: Partial<Record<SchoolProfileRequiredKey, string | null | undefined>>,
): Partial<Record<SchoolProfileRequiredKey, string>> {
  const r = schoolProfileRequiredSchema.safeParse({
    email: values.email ?? "",
    phone: values.phone ?? "",
    address: values.address ?? "",
    city: values.city ?? "",
    state: values.state ?? "",
    pincode: values.pincode ?? "",
  });
  if (r.success) return {};
  const out: Partial<Record<SchoolProfileRequiredKey, string>> = {};
  for (const issue of r.error.issues) {
    const key = issue.path[0] as SchoolProfileRequiredKey | undefined;
    if (key && !out[key]) out[key] = issue.message;
  }
  return out;
}

/** `POST /onboarding/quickstart` — the whole of signup, in one step. */
export const quickStartSchema = z.object({
  pendingToken: z.string().optional(),
  // Required on "path B" (an already-authenticated user with no
  // pendingToken completing onboarding) — the same Control-Center-issued
  // code /auth/signup/start checks. Path A carries its code inside the
  // pendingToken instead, so it's optional here.
  signupCode:   optionalText(40),
  name:         z.string().trim().min(2, "School name is required").max(200),
  schoolCode:   optionalText(40),
  board:        optionalText(60),
  profile:      schoolProfileSchema,
}).refine((d) => Boolean(d.pendingToken) || Boolean(d.signupCode), {
  message: "An access code is required.",
  path: ["signupCode"],
});

export type QuickStartRequest = z.infer<typeof quickStartSchema>;

/**
 * The response carries NO subdomain and no site fields: the client lands on
 * /dashboard on this origin, it does not navigate off to a tenant host.
 *
 * It DOES carry the full session payload, because the web client hands it
 * straight to the auth store's `login()`, whose `AuthUser` requires `school`
 * and `schools` — not optionally. A first draft of this type listed only
 * id/name/email/role; an account created through it would have signed in with
 * no active school, and every consumer that reads `user.school.id` (Sentry
 * scope, `activeSchoolId` seeding, `useSchoolSetupStatus`) would have gone
 * quietly undefined. Keep this in step with `AuthUser` in
 * apps/web/src/store/auth-store.ts.
 */
export interface QuickStartSchoolRef {
  id: string;
  name: string;
  code: string;
  logo?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface QuickStartResponse {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    avatar?: string | null;
    onboardingComplete?: boolean;
    school: QuickStartSchoolRef;
    schools: QuickStartSchoolRef[];
  };
  school: QuickStartSchoolRef;
  activeSchoolId: string | null;
  accessToken: string;
  refreshToken: string;
}
